import React, { Fragment, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import Switch from 'react-switch';
import omit from 'lodash.omit';

import DropdownMenu from '../../../../../../DropdownMenu';
import RangeSlider from '../../../../../../RangeSlider';
import { CARD_ELEMENT_TYPE } from '../../../../../../../../services/cards/constants';
import Input from '../../../../../../Input';
import CardDesignerCardElementColorControl from '../CardDesignerCardElementColorControl';
import BoxShadowIcon from '../../../../../../../icons/BoxShadowIcon';
import OpacityIcon from '../../../../../../../icons/OpacityIcon';

const propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string,
    cardElement: PropTypes.shape({
      type: PropTypes.oneOf(Object.values(CARD_ELEMENT_TYPE)),
    }),
    cardElementConfiguration: PropTypes.shape({
      box_shadow: PropTypes.shape({
        offset_x: PropTypes.number,
        offset_y: PropTypes.number,
        color: PropTypes.string,
        opacity: PropTypes.number,
        blur_radius: PropTypes.number,
      }),
      text_shadow: PropTypes.shape({
        offset_x: PropTypes.number,
        offset_y: PropTypes.number,
        color: PropTypes.string,
        opacity: PropTypes.number,
        blur_radius: PropTypes.number,
      }),
    }),
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardDesignerCardElementBoxShadowControls = ({
  element,
  onElementConfigurationChange,
}) => {
  const { cardElement, cardElementConfiguration } = element;
  const { type } = cardElement;
  const elementShadow =
    cardElementConfiguration?.box_shadow ??
    cardElementConfiguration?.text_shadow;
  // In theory only one of these will be defined. It's either boxShadow for photo, or textShadow for text
  const {
    offset_x: shadowOffsetX,
    offset_y: shadowOffsetY,
    opacity: shadowOpacity,
    blur_radius: shadowBlurRadius,
    color: shadowColor,
  } = elementShadow ?? {};

  const baseShadow = useMemo(
    () => ({
      offset_x: 0,
      offset_y: 0,
      color: '#000',
      opacity: 0.5,
      blur_radius: 0,
    }),
    [],
  );

  const [blurRadius, setBlurRadius] = useState(shadowBlurRadius ?? 0);
  const [xOffset, setXOffset] = useState(shadowOffsetX ?? 0);
  const [yOffset, setYOffset] = useState(shadowOffsetY ?? 0);
  const [opacity, setOpacity] = useState(shadowOpacity ?? 0.5);

  useEffect(() => {
    setBlurRadius(shadowBlurRadius ?? 0);
  }, [shadowBlurRadius]);
  useEffect(() => {
    setXOffset(shadowOffsetX ?? 0);
  }, [shadowOffsetX]);
  useEffect(() => {
    setYOffset(shadowOffsetY ?? 0);
  }, [shadowOffsetY]);
  useEffect(() => {
    setOpacity(shadowOpacity ?? 0);
  }, [shadowOpacity]);

  return (
    <DropdownMenu
      placement="bottom-start"
      dropdownClassName="card-designer-card-element-common-control-dropdown card-designer-card-element-box-shadow-controls-dropdown"
      toggleClassName="ml-1 d-flex align-items-center"
      toggle={
        <span
          data-for="drop-shadow-controls"
          data-place="bottom"
          data-effect="solid"
          data-tip
        >
          <BoxShadowIcon size={22} />
          <ReactTooltip id="drop-shadow-controls" className="tooltip">
            Drop Shadow
          </ReactTooltip>
        </span>
      }
    >
      <div className="p-2 w-100 d-flex align-items-center justify-content-between text-dark">
        Drop shadow{' '}
        <Switch
          checked={!!elementShadow}
          uncheckedIcon={false}
          checkedIcon={false}
          height={25}
          width={40}
          onColor="#7ace21"
          offColor="#cdd6e4"
          onChange={() => {
            if (elementShadow) {
              onElementConfigurationChange({
                element,
                elementConfig: {
                  box_shadow: null,
                  text_shadow: null,
                },
              });
            } else {
              onElementConfigurationChange({
                element,
                elementConfig: {
                  box_shadow:
                    type === CARD_ELEMENT_TYPE.PHOTO ? baseShadow : undefined,
                  text_shadow:
                    type === CARD_ELEMENT_TYPE.TEXT ? baseShadow : undefined,
                },
              });
            }
          }}
        />
      </div>
      {!!elementShadow && (
        <Fragment>
          <hr className="w-100 my-1" />
          <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
            <span className="mr-1">
              <BoxShadowIcon id="box-shadow-icon-b" size={16} />
            </span>
            <RangeSlider
              className="mx-2"
              onChange={values => {
                setBlurRadius(parseInt(values[0], 10));
              }}
              onFinalChange={values => {
                const shadowConfig = {
                  ...baseShadow,
                  ...omit(elementShadow, ['__typename']),
                  blur_radius: parseInt(values[0], 10),
                };

                onElementConfigurationChange({
                  element,
                  elementConfig: {
                    box_shadow:
                      type === CARD_ELEMENT_TYPE.PHOTO
                        ? shadowConfig
                        : undefined,
                    text_shadow:
                      type === CARD_ELEMENT_TYPE.TEXT
                        ? shadowConfig
                        : undefined,
                  },
                });
              }}
              values={[Math.round(blurRadius)]}
              min={0}
              max={99}
            />
            <Input
              id="blur-radius"
              className="mr-1"
              type="number"
              max={99}
              min={0}
              step={1}
              value={Math.round(blurRadius)}
              onChange={({ target }) => {
                setBlurRadius(parseInt(target.value, 10));
                const shadowConfig = {
                  ...baseShadow,
                  ...omit(elementShadow, ['__typename']),
                  blur_radius: parseInt(target.value, 10),
                };
                onElementConfigurationChange({
                  element,
                  elementConfig: {
                    box_shadow:
                      type === CARD_ELEMENT_TYPE.PHOTO
                        ? shadowConfig
                        : undefined,
                    text_shadow:
                      type === CARD_ELEMENT_TYPE.TEXT
                        ? shadowConfig
                        : undefined,
                  },
                });
              }}
            />{' '}
            <span className="input-addon-text">px</span>
          </div>
          <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
            <span className="mr-1">
              <OpacityIcon size={16} />
            </span>
            <RangeSlider
              className="mx-2"
              onChange={values =>
                setOpacity(Math.round((values[0] / 1e2) * 1e2) / 1e2)
              }
              onFinalChange={values => {
                const percentValue = Math.round((values[0] / 1e2) * 1e2) / 1e2;
                setOpacity(percentValue);
                const shadowConfig = {
                  ...baseShadow,
                  ...omit(elementShadow, ['__typename']),
                  opacity,
                };

                onElementConfigurationChange({
                  element,
                  elementConfig: {
                    box_shadow:
                      type === CARD_ELEMENT_TYPE.PHOTO
                        ? shadowConfig
                        : undefined,
                    text_shadow:
                      type === CARD_ELEMENT_TYPE.TEXT
                        ? shadowConfig
                        : undefined,
                  },
                });
              }}
              values={[Math.round(opacity * 1e2)]}
              min={0}
              max={100}
            />
            <Input
              id="opacity"
              className="mr-1"
              value={Math.round(opacity * 1e2)}
              type="number"
              max={100}
              min={0}
              onChange={({ target }) => {
                const percentValue =
                  Math.round((target.value / 1e2) * 1e2) / 1e2;
                setOpacity(percentValue);
                const shadowConfig = {
                  ...baseShadow,
                  ...omit(elementShadow, ['__typename']),
                  opacity,
                };
                onElementConfigurationChange({
                  element,
                  elementConfig: {
                    box_shadow:
                      type === CARD_ELEMENT_TYPE.PHOTO
                        ? shadowConfig
                        : undefined,
                    text_shadow:
                      type === CARD_ELEMENT_TYPE.TEXT
                        ? shadowConfig
                        : undefined,
                  },
                });
              }}
            />{' '}
            <span className="input-addon-text">%</span>
          </div>
          <div className="p-2 d-flex w-100 align-items-center justify-content-between">
            <div className="offset-input">
              <span>X</span>
              <Input
                id="x-offset"
                type="number"
                className="mr-1"
                max={99}
                min={-99}
                step={1}
                value={xOffset}
                onChange={({ target }) => {
                  setXOffset(parseInt(target.value, 10));
                  const shadowConfig = {
                    ...baseShadow,
                    ...omit(elementShadow, ['__typename']),
                    offset_x: parseInt(target.value, 10),
                  };
                  onElementConfigurationChange({
                    element,
                    elementConfig: {
                      box_shadow:
                        type === CARD_ELEMENT_TYPE.PHOTO
                          ? shadowConfig
                          : undefined,
                      text_shadow:
                        type === CARD_ELEMENT_TYPE.TEXT
                          ? shadowConfig
                          : undefined,
                    },
                  });
                }}
              />{' '}
              <span className="input-addon-text">px</span>
            </div>
            <div className="offset-input">
              <span>Y</span>
              <Input
                id="y-offset"
                type="number"
                className="mr-1"
                max={99}
                min={-99}
                step={1}
                value={yOffset}
                onChange={({ target }) => {
                  setYOffset(parseInt(target.value, 10));
                  const shadowConfig = {
                    ...baseShadow,
                    ...omit(elementShadow, ['__typename']),
                    offset_y: parseInt(target.value, 10),
                  };
                  onElementConfigurationChange({
                    element,
                    elementConfig: {
                      box_shadow:
                        type === CARD_ELEMENT_TYPE.PHOTO
                          ? shadowConfig
                          : undefined,
                      text_shadow:
                        type === CARD_ELEMENT_TYPE.TEXT
                          ? shadowConfig
                          : undefined,
                    },
                  });
                }}
              />{' '}
              <span className="input-addon-text">px</span>
            </div>
          </div>
          <div className="p-2 d-flex w-100 align-items-center ml-auto text-sm text-nowrap">
            Shadow Color
            <CardDesignerCardElementColorControl
              className="ml-auto"
              onChange={hex => {
                const shadowConfig = {
                  ...baseShadow,
                  ...omit(elementShadow, ['__typename']),
                  color: hex,
                };

                onElementConfigurationChange({
                  element,
                  elementConfig: {
                    box_shadow:
                      type === CARD_ELEMENT_TYPE.PHOTO
                        ? shadowConfig
                        : undefined,
                    text_shadow:
                      type === CARD_ELEMENT_TYPE.TEXT
                        ? shadowConfig
                        : undefined,
                  },
                });
              }}
              defaultColor={shadowColor?.color ?? undefined}
            />
          </div>
        </Fragment>
      )}
    </DropdownMenu>
  );
};

CardDesignerCardElementBoxShadowControls.propTypes = propTypes;

export default CardDesignerCardElementBoxShadowControls;
